import React, { useState, useEffect } from 'react';
/* eslint-disable react/no-unescaped-entities, no-empty */
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Check, Lock, Star, Trophy, ChevronRight, X, Sparkles, Share2 } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { useAuth, useLanguage, api } from '../context/AppContext';
import ChallengeShareModal from './ChallengeShareModal';
import Day7CoachingCTA from './Day7CoachingCTA';

export function WelcomeConfetti({ show, onDone }) {
  useEffect(() => {
    if (!show) return;
    const duration = 4000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#D4FF00', '#FFD700', '#FF6B35', '#FFFFFF'] });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#D4FF00', '#FFD700', '#FF6B35', '#FFFFFF'] });
      if (Date.now() < end) requestAnimationFrame(frame);
      else if (onDone) setTimeout(onDone, 1000);
    };
    frame();
    confetti({ particleCount: 150, spread: 120, origin: { y: 0.6 }, colors: ['#D4FF00', '#FFD700', '#FF6B35', '#A855F7', '#3B82F6'], scalar: 1.3 });
  }, [show, onDone]);

  if (!show) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={onDone}
      >
        <motion.div
          initial={{ scale: 0.5, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', damping: 12 }}
          className="bg-card border-2 border-primary rounded-2xl p-8 mx-4 text-center max-w-sm shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4">
            <Rocket className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-heading text-2xl uppercase tracking-tight text-primary mb-2">
            Bienvenue chez YUMIX!
          </h2>
          <p className="text-muted-foreground text-sm mb-4">
            YUMIX t'a prepare un defi personnalise de 7 jours. Complete chaque mission pour debloquer des points et prouver que tu es un vrai YUMIXER!
          </p>
          <Button onClick={onDone} className="bg-primary text-primary-foreground font-heading uppercase w-full" data-testid="welcome-start-btn">
            <Sparkles className="w-5 h-5 mr-2" /> C'est parti!
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function WelcomeChallengeCard({ onMissionClick, priorityMode = false } = {}) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const fr = language === 'fr';
  const es = language === 'es';
  const L = (frTxt, enTxt, esTxt) => es ? esTxt : (fr ? frTxt : enTxt);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCoachingCTA, setShowCoachingCTA] = useState(false);
  const [coachingCTALink, setCoachingCTALink] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/welcome-challenge/status');
      if (res.data.active) setChallenge(res.data.challenge);
      // Day 7 coaching CTA (French only, no coach assigned)
      if (res.data.show_coaching_cta) {
        setShowCoachingCTA(true);
        setCoachingCTALink(res.data.coaching_cta_link || '');
      }
    } catch {}
    finally { setLoading(false); }
  };

  const checkDay = async () => {
    setChecking(true);
    try {
      const res = await api.post('/welcome-challenge/check-day');
      if (res.data.completed) {
        toast.success(
          L(
            `Jour ${res.data.day} complete! +${res.data.points} pts`,
            `Day ${res.data.day} complete! +${res.data.points} pts`,
            `¡Día ${res.data.day} completado! +${res.data.points} pts`
          ),
          { duration: 5000 }
        );
        if (res.data.challenge_complete) {
          confetti({ particleCount: 200, spread: 120, origin: { y: 0.6 }, colors: ['#FFD700', '#D4FF00', '#FF6B35'] });
          toast.success(
            L(
              'DÉFI PRISE EN MAIN COMPLETE! +200 pts bonus!',
              '7-DAY CHALLENGE COMPLETE! +200 bonus pts!',
              '¡DESAFÍO DE 7 DÍAS COMPLETO! +200 pts bonus!'
            ),
            { duration: 8000 }
          );
          // Show share modal after a short delay
          setTimeout(() => setShowShareModal(true), 2000);
        }
        fetchStatus();
      } else {
        if (res.data.check === 'protein_target_hit' && res.data.target_protein_g) {
          toast.info(
            fr 
              ? `Proteines: ${res.data.current_protein_g || 0}g / ${res.data.target_protein_g}g — Continue, tu y es presque!`
              : `Protein: ${res.data.current_protein_g || 0}g / ${res.data.target_protein_g}g — Keep going!`
          );
        } else {
          toast.info(L(
            `Mission du jour ${res.data.day} pas encore completee. Continue!`,
            `Day ${res.data.day} mission not yet complete. Keep going!`,
            `Misión del día ${res.data.day} aún no completada. ¡Sigue!`
          ));
        }
      }
    } catch {}
    finally { setChecking(false); }
  };

  if (loading || !challenge || challenge.expired || dismissed) return null;

  // Hide the celebration banner permanently once the user has shared
  // OR explicitly dismissed it via the X button.
  if (challenge.is_complete && (challenge.shared || challenge.dismissed)) return null;

  const dismissCompleteWidget = async () => {
    setDismissed(true); // optimistic UI
    try {
      await api.post('/welcome-challenge/dismiss-complete');
    } catch {
      // Non-blocking — local dismiss already applied
    }
  };

  // Show share button for completed challenges
  if (challenge.is_complete) {
    return (
      <>
        <Card className="mb-6 bg-gradient-to-br from-amber-500/10 via-card to-orange-500/5 border-amber-500/30 overflow-hidden" data-testid="welcome-challenge-complete">
          <div className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
              <Trophy className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground">
                {L('Défi 7 Jours Complete !', '7-Day Challenge Complete!', '¡Desafío 7 Días Completo!')}
              </p>
              <p className="text-xs text-muted-foreground">
                {challenge.total_points_earned} pts {L('gagnes', 'earned', 'ganados')}
              </p>
            </div>
            <Button
              onClick={() => setShowShareModal(true)}
              size="sm"
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-black font-heading uppercase text-xs shrink-0"
              data-testid="share-complete-challenge-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              {L('Partager', 'Share', 'Compartir')}
            </Button>
            <button
              onClick={dismissCompleteWidget}
              className="text-muted-foreground/40 hover:text-muted-foreground p-1 shrink-0"
              aria-label={L('Fermer', 'Dismiss', 'Cerrar')}
              data-testid="dismiss-complete-challenge-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </Card>
        <ChallengeShareModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          onShared={() => { setDismissed(true); fetchStatus(); }}
          challengeData={challenge}
          userName={user?.name || user?.email}
        />
      </>
    );
  }

  const completedCount = challenge.days_completed?.length || 0;
  const progress = (completedCount / 7) * 100;

  // Template-based colors
  const colorMap = {
    green: { bg: 'from-green-500/10 via-card to-emerald-500/5', border: 'border-green-500/30', text: 'text-green-500', accent: 'bg-green-500/15', ring: 'ring-green-500/30' },
    pink: { bg: 'from-pink-500/10 via-card to-fuchsia-500/5', border: 'border-pink-500/30', text: 'text-pink-500', accent: 'bg-pink-500/15', ring: 'ring-pink-500/30' },
    red: { bg: 'from-red-500/10 via-card to-orange-500/5', border: 'border-red-500/30', text: 'text-red-500', accent: 'bg-red-500/15', ring: 'ring-red-500/30' },
    amber: { bg: 'from-amber-500/10 via-card to-yellow-500/5', border: 'border-amber-500/30', text: 'text-amber-500', accent: 'bg-amber-500/15', ring: 'ring-amber-500/30' },
    orange: { bg: 'from-primary/10 via-card to-orange-500/5', border: 'border-primary/30', text: 'text-primary', accent: 'bg-primary/15', ring: 'ring-primary/30' },
  };
  const tc = colorMap[challenge.color] || colorMap.orange;

  return (
    <>
    <Card className={`mb-6 bg-gradient-to-br ${tc.bg} ${tc.border} overflow-hidden`} data-testid="welcome-challenge-card">
      {/* Iter. 406.r — Priority unlock message for new users */}
      {priorityMode && !challenge.is_complete && (
        <div
          className="relative bg-gradient-to-r from-amber-500/30 via-amber-400/40 to-amber-500/30 border-b border-amber-300/40 px-4 py-2 flex items-center gap-2 text-center"
          data-testid="welcome-challenge-priority-badge"
        >
          <span className="text-base" aria-hidden="true">🔥</span>
          <p className="flex-1 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-amber-100 leading-tight">
            {fr
              ? 'PRIORITÉ : Complète ton défi pour débloquer ton cycle d\'entraînement 6 semaines et toutes les fonctions YUMIX.'
              : 'PRIORITY: Complete your challenge to unlock the 6-week training cycle and all YUMIX features.'}
          </p>
          <span className="text-base" aria-hidden="true">🔥</span>
        </div>
      )}
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${tc.accent} flex items-center justify-center`}>
              <span className="text-lg">{challenge.emoji || '🎯'}</span>
            </div>
            <div>
              {/* Iter. 406.fr.60 — titre en `text-foreground` (blanc lisible)
                  au lieu du `tc.text` coloré : le rouge/vert sur fond glow
                  du même ton était illisible. On garde l'icône/accent colorés. */}
              <h3 className="text-sm font-black uppercase tracking-wide text-foreground">
                {es ? (challenge.title_es || challenge.title_en) : (fr ? challenge.title_fr : challenge.title_en)}
              </h3>
              <p className="text-[10px] text-muted-foreground">
                {fr
                  ? `Jour ${challenge.current_day}/7 — ${completedCount} mission${completedCount > 1 ? 's' : ''} complétée${completedCount > 1 ? 's' : ''}`
                  : `Day ${challenge.current_day}/7 — ${completedCount} mission${completedCount > 1 ? 's' : ''} completed`}
              </p>
            </div>
          </div>
          <button onClick={() => setDismissed(true)} className="text-muted-foreground/30 hover:text-muted-foreground p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Progress value={progress} className="h-2.5 mb-3 bg-secondary" />

        <div className="grid grid-cols-7 gap-1 mb-3">
          {challenge.days.map((day) => (
            <div
              key={day.day}
              className={`flex flex-col items-center p-1.5 rounded-lg text-center transition-all ${
                day.completed
                  ? 'bg-emerald-500/15 border border-emerald-500/30'
                  : day.is_current
                    ? `bg-primary/15 border border-primary/40 ${tc.ring} ring-1`
                    : day.is_locked
                      ? 'bg-secondary/30 opacity-40'
                      : 'bg-secondary/50'
              }`}
              data-testid={`welcome-day-${day.day}`}
            >
              <span className="text-[9px] font-bold text-muted-foreground">J{day.day}</span>
              {day.completed ? (
                <Check className="w-3.5 h-3.5 text-emerald-500" />
              ) : day.is_locked ? (
                <Lock className="w-3 h-3 text-muted-foreground/30" />
              ) : (
                <Star className="w-3.5 h-3.5 text-primary" />
              )}
              <span className="text-[8px] text-muted-foreground">{day.points}pt</span>
            </div>
          ))}
        </div>

        {/* Current mission */}
        {challenge.days.find(d => d.is_current) && (() => {
          const currentDay = challenge.days.find(d => d.is_current);
          // Iter. 405.h — J5 (workout onboarding) hijacks the action:
          // clicking the CTA opens the WorkoutOnboardingModal wizard
          // instead of running the generic checkDay (which only passes
          // if a workout is already logged). Wire happens through the
          // optional `onMissionClick` prop wired up by Dashboard.
          const isOnboardingJ5 = currentDay?.day === 5 && typeof onMissionClick === 'function';
          return (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40 mb-3">
            <Star className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-foreground">
                {L('Mission du jour:', "Today's mission:", 'Misión del día:')}
              </p>
              <p className="text-sm text-muted-foreground">
                {es ? (currentDay?.mission_es || currentDay?.mission_en) : (fr ? currentDay?.mission_fr : currentDay?.mission_en)}
              </p>
            </div>
            <Button
              onClick={isOnboardingJ5 ? () => onMissionClick(currentDay.day) : checkDay}
              disabled={checking}
              size="sm"
              className="bg-primary text-primary-foreground flex-shrink-0"
              data-testid={isOnboardingJ5 ? 'welcome-j5-start-btn' : 'welcome-check-btn'}
            >
              {checking
                ? '...'
                : isOnboardingJ5
                ? <>{L('Démarrer', 'Start', 'Comenzar')}</>
                : <><Check className="w-4 h-4 mr-1" /> {L('Valider', 'Check', 'Validar')}</>}
            </Button>
          </div>
          );
        })()}

        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <Trophy className="w-3 h-3 text-primary" />
          <span>{es ? (challenge.reward_es || challenge.reward_en) : (fr ? challenge.reward_fr : challenge.reward_en)}</span>
          <span className="ml-auto font-bold text-primary">{challenge.total_points_earned} pts</span>
        </div>
      </div>
    </Card>
    <ChallengeShareModal
      show={showShareModal}
      onClose={() => setShowShareModal(false)}
      onShared={() => { setDismissed(true); fetchStatus(); }}
      challengeData={challenge}
      userName={user?.name || user?.email}
    />
    <Day7CoachingCTA
      show={showCoachingCTA}
      calendlyLink={coachingCTALink}
      onDismiss={() => setShowCoachingCTA(false)}
    />
  </>
  );
}
